import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { Team } from 'app/library/team/models/team.model';
import { TeamService } from 'app/library/team/services/team.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { IEntityNode } from 'app/projects/shared/src/lib/interfaces/entity-node.interface';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-certificates-teams-filter',
    templateUrl: './certificates-teams-filter.component.html',
    styleUrls: ['./certificates-teams-filter.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
})
export class CertificatesTeamsFilterComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    teamId: string;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    treeControl = new NestedTreeControl<IEntityNode>((node) => node.children);
    dataSource = new MatTreeNestedDataSource<IEntityNode>();
    list: IChoosenFilterInterface[] = [];
    isLoading = true;
    originalData: IEntityNode[] = [];
    excludedTeamIds: string[] = [];

    constructor(private _teamService: TeamService, private _allUsersService: AllUsersApiService, private _translateService: TranslateService) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._syncExcludedTeamIdsFromList();
            this.updateSelectionState();
        });

        if (this.teamId) {
            const children = await this._teamService.fetchParentChildren(this.teamId);
            this.buildTreeFromTeams(children || []);
            this.isLoading = false;
        } else {
            this._teamService.rootTeams$
                .pipe(
                    filter((teams) => !!teams),
                    takeUntil(this._unsubscribeAll)
                )
                .subscribe((teams) => {
                    this.buildTreeFromTeams(teams);
                    this.isLoading = false;
                });

            await this._teamService.fetchRootTeams();
        }
    }

    hasChild = (_: number, node: IEntityNode) => (!!node.children && node.children.length > 0) || node.childrenCount > 0;

    async toggleNode(node: IEntityNode): Promise<void> {
        if (this.treeControl.isExpanded(node)) {
            this.treeControl.collapse(node);
            return;
        }

        if (node.childrenCount > 0 && (!node.children || node.children.length === 0)) {
            node.isLoading = true;
            this.treeControl.expand(node);

            const children = await this._teamService.fetchParentChildren(node.id);
            if (children) {
                node.children = children.map((child) => this.mapTeamToNode(child, node.id));
                node.children.forEach((childNode) => {
                    childNode.isSelected = this._isTeamNodeSelected(childNode);
                });
            }

            node.isLoading = false;
            this.refreshDataSource();
            this.treeControl.expand(node);
            this.originalData = this.collectAllNodes();
        } else {
            this.treeControl.expand(node);
        }
    }

    addToList(node: IEntityNode): void {
        if (this._hasSelectedAncestor(node)) {
            this._includeTeamInSelectedParent(node);
            return;
        }

        const selectedTeamAndDescendantIds = [node.id, ...this._getKnownDescendantIds(node)];
        this._emitUpdatedList([
            ...this.list.filter((item) => item.entity.type !== 'Teams' || !selectedTeamAndDescendantIds.includes(item.entity.id)),
            {
                entity: { id: node.id, type: 'Teams', excludedTeamIds: [] },
                valueToShow: this._buildTeamValueToShow(node.name),
            },
        ]);
    }

    removeFromList(node: IEntityNode): void {
        if (this._isExplicitTeamSelected(node.id)) {
            const selectedTeamAndDescendantIds = [node.id, ...this._getKnownDescendantIds(node)];
            this._emitUpdatedList(this.list.filter((item) => item.entity.type !== 'Teams' || !selectedTeamAndDescendantIds.includes(item.entity.id)));
            return;
        }

        if (this._hasSelectedAncestor(node)) {
            this._excludeTeamFromSelectedParent(node);
            return;
        }

        this._emitUpdatedList(this.list.filter((item) => item.entity.id !== node.id));
    }

    private buildTreeFromTeams(teams: Team[]): void {
        const listTree: IEntityNode[] = teams.map((team) => this.mapTeamToNode(team, team.parent?.id));
        this.dataSource.data = listTree;
        this.originalData = listTree;
    }

    private mapTeamToNode(team: Team, parentId?: string): IEntityNode {
        return {
            id: team.id,
            name: team.name,
            children: [],
            childrenCount: team.subteamCount || 0,
            parent: parentId ? { id: parentId } : team.parent,
            isSelected: this._isExplicitTeamSelected(team.id),
        };
    }

    private updateSelectionState(): void {
        const updateNodes = (nodes: IEntityNode[]): void => {
            nodes.forEach((node) => {
                node.isSelected = this._isTeamNodeSelected(node);
                if (node.children?.length) {
                    updateNodes(node.children);
                }
            });
        };

        if (this.originalData.length) {
            updateNodes(this.originalData);
        }

        if (this.dataSource.data?.length) {
            updateNodes(this.dataSource.data);
        }

        if (this.originalData.length || this.dataSource.data?.length) {
            this.refreshDataSource();
        }
    }

    private refreshDataSource(): void {
        const data = this.dataSource.data;
        this.dataSource.data = null;
        this.dataSource.data = data;
    }

    private collectAllNodes(): IEntityNode[] {
        const collect = (nodes: IEntityNode[]): IEntityNode[] => {
            return nodes.map((node) => ({
                ...node,
                children: node.children ? collect(node.children) : [],
            }));
        };
        return collect(this.dataSource.data);
    }

    private _emitUpdatedList(updatedList: IChoosenFilterInterface[]): void {
        this.list = updatedList;
        this._syncExcludedTeamIdsFromList();
        this.updateSelectionState();
        this.addedToList.emit(updatedList);
    }

    private _syncExcludedTeamIdsFromList(): void {
        const excludedTeamIds = this.list.filter((item) => item.entity.type === 'Teams').reduce<string[]>((acc, item) => [...acc, ...(item.entity.excludedTeamIds || [])], []);

        this.excludedTeamIds = Array.from(new Set(excludedTeamIds));
    }

    private _buildTeamValueToShow(teamName: string, excludedTeamIds: string[] = []): string {
        const label = excludedTeamIds.length ? this._translateService.instant('TEAMS.LABEL.PARTIAL_SELECTION', { teamName }) : teamName;
        return `${this._translateService.instant('TEAMS.TITLE.PLURAL')}: ${label}`;
    }

    private _isExplicitTeamSelected(teamId: string): boolean {
        return this.list.some((item) => item.entity.id === teamId && item.entity.type === 'Teams');
    }

    private _getTeamFilter(teamId: string): IChoosenFilterInterface | undefined {
        return this.list.find((item) => item.entity.id === teamId && item.entity.type === 'Teams');
    }

    private _isTeamNodeSelected(node: IEntityNode): boolean {
        const path = this._getNodePath(node);
        return this._isPathSelected(path.length ? path : [node]);
    }

    private _hasSelectedAncestor(node: IEntityNode): boolean {
        return !!this._getSelectedAncestorId(this._getNodePath(node));
    }

    private _isPathSelected(path: { id: string }[]): boolean {
        const selectedAncestorId = this._getSelectedAncestorId(path);
        if (!selectedAncestorId) {
            return false;
        }

        const selectedIndex = path.findIndex((item) => item.id === selectedAncestorId);
        const descendantPath = path.slice(selectedIndex + 1);
        return !descendantPath.some((item) => this.excludedTeamIds.includes(item.id));
    }

    private _getSelectedAncestorId(path: { id: string }[]): string {
        const selectedAncestor = path.find((item) => this._isExplicitTeamSelected(item.id));
        return selectedAncestor?.id;
    }

    private _excludeTeamFromSelectedParent(node: IEntityNode): void {
        const path = this._getNodePath(node);
        const selectedAncestorId = this._getSelectedAncestorId(path);
        const selectedTeamFilter = selectedAncestorId ? this._getTeamFilter(selectedAncestorId) : null;

        if (!selectedTeamFilter || selectedTeamFilter.entity.id === node.id) {
            return;
        }

        const descendantIds = this._getKnownDescendantIds(node);
        const nextExcludedTeamIds = Array.from(new Set([...(selectedTeamFilter.entity.excludedTeamIds || []).filter((teamId) => !descendantIds.includes(teamId)), node.id]));

        this._updateSelectedTeamFilterExclusions(selectedTeamFilter.entity.id, nextExcludedTeamIds);
    }

    private _includeTeamInSelectedParent(node: IEntityNode): void {
        const path = this._getNodePath(node);
        const selectedAncestorId = this._getSelectedAncestorId(path);
        const selectedTeamFilter = selectedAncestorId ? this._getTeamFilter(selectedAncestorId) : null;

        if (!selectedTeamFilter) {
            return;
        }

        const pathIds = path.map((pathItem) => pathItem.id);
        const descendantIds = this._getKnownDescendantIds(node);
        const excludedTeamIdsToRemove = [...pathIds, ...descendantIds];
        const nextExcludedTeamIds = (selectedTeamFilter.entity.excludedTeamIds || []).filter((teamId) => !excludedTeamIdsToRemove.includes(teamId));

        this._updateSelectedTeamFilterExclusions(selectedTeamFilter.entity.id, nextExcludedTeamIds);
    }

    private _updateSelectedTeamFilterExclusions(selectedTeamId: string, excludedTeamIds: string[]): void {
        this._emitUpdatedList(
            this.list.map((item) => {
                if (item.entity.type !== 'Teams' || item.entity.id !== selectedTeamId) {
                    return item;
                }

                return {
                    ...item,
                    entity: {
                        ...item.entity,
                        excludedTeamIds,
                    },
                    valueToShow: this._buildTeamValueToShow(this._getTeamNameFromValueToShow(item.valueToShow), excludedTeamIds),
                };
            })
        );
    }

    private _getTeamNameFromValueToShow(valueToShow: string): string {
        const idx = valueToShow.indexOf(': ');
        const label = idx === -1 ? valueToShow : valueToShow.slice(idx + 2);
        const partialSuffix = this._translateService.instant('TEAMS.LABEL.PARTIAL_SELECTION', { teamName: '' });
        return label.endsWith(partialSuffix) ? label.slice(0, -partialSuffix.length) : label;
    }

    private _getNodePath(node: IEntityNode): IEntityNode[] {
        const path = this._findNodePath(this.dataSource.data, node.id);
        return path || [node];
    }

    private _findNodePath(nodes: IEntityNode[], teamId: string, parents: IEntityNode[] = []): IEntityNode[] | null {
        for (const node of nodes) {
            const path = [...parents, node];
            if (node.id === teamId) {
                return path;
            }

            if (node.children?.length) {
                const childPath = this._findNodePath(node.children, teamId, path);
                if (childPath) {
                    return childPath;
                }
            }
        }

        return null;
    }

    private _findNodeById(teamId: string): IEntityNode | null {
        const find = (nodes: IEntityNode[]): IEntityNode | null => {
            for (const node of nodes) {
                if (node.id === teamId) {
                    return node;
                }

                const child = node.children?.length ? find(node.children) : null;
                if (child) {
                    return child;
                }
            }

            return null;
        };

        return find(this.dataSource.data);
    }

    private _getKnownDescendantIds(node: IEntityNode): string[] {
        const treeNode = this._findNodeById(node.id);
        if (!treeNode?.children?.length) {
            return [];
        }

        const collect = (nodes: IEntityNode[]): string[] => {
            return nodes.reduce<string[]>((acc, child) => [...acc, child.id, ...(child.children?.length ? collect(child.children) : [])], []);
        };

        return collect(treeNode.children);
    }
}
